if (!customElements.get('subscription-popup')) {
  customElements.define(
    'subscription-popup',
    class SubscriptionPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-trigger-open', 'data-repeat-open']);
      }

      constructor() {
        super();
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
        // Each popup tracks its own "already shown" state. The global popup keeps
        // the original key; a template-scoped (local) popup gets its own key so
        // dismissing one doesn't suppress the other.
        this.cookieName = this.dataset.cookieKey || 'hypertheme:popup';
        this.popupScope = this.dataset.nwPopupScope || 'global';

        // Configurable thresholds — read from data attributes set by Liquid.
        // Falls back to the original hardcoded values so existing deployments
        // that don't yet have the new schema settings are unaffected.
        this.triggerDelayMs = (parseInt(this.dataset.triggerDelay, 10) || 5) * 1000;
        this.triggerScrollPercent = parseInt(this.dataset.triggerScrollPercent, 10) || 30;

        window.FoxTheme.DOMready(this.handleOpen.bind(this));
      }

      get focusElement() {
        return this.querySelector('[data-dialog-focus]') || this;
      }

      prepareToShow() {
        super.prepareToShow();
        this.setRepeatOpen();

        const focusTarget = this.querySelector('[data-dialog-focus]');
        if (focusTarget) focusTarget.focus();
      }

      handleOpen() {
        if (Shopify.designMode) return;

        // Global popup steps aside if a template-scoped popup is present on this
        // page — so a bespoke per-template popup wins, with no exclusion list.
        if (this.popupScope !== 'local' &&
            document.querySelector('subscription-popup[data-nw-popup-scope="local"]')) {
          return;
        }

        if (this.repeatOpen !== 'always_repeat') {
          var savedData = FoxTheme.utils.getStorage(this.cookieName);
          if (savedData && savedData.opened) {
            return false;
          }
        }

        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(function () {
              this.show();
            }.bind(this), this.triggerDelayMs);
            break;

          case 'scroll_down':
            this.triggerAfterScrollPercent(this.triggerScrollPercent, this.show.bind(this));
            break;

          // Races delay and scroll — whichever fires first opens the popup.
          // The `triggered` flag ensures only one of the two wins.
          case 'delay_or_scroll':
            var triggered = false;

            var fireOnce = function () {
              if (triggered) return;
              triggered = true;
              this.show();
            }.bind(this);

            setTimeout(fireOnce, this.triggerDelayMs);
            this.triggerAfterScrollPercent(this.triggerScrollPercent, fireOnce);
            break;

          default:
            this.show();
            break;
        }
      }

      // ─── Scroll trigger: percentage-based ───────────────────────────────────
      //
      // Replaces the original pixel-based triggerAfterScroll().
      // Percentage is calculated against document.documentElement.scrollHeight
      // minus the viewport height — i.e. the actual scrollable distance —
      // so 30% means "scrolled 30% of the way down the page", matching the
      // behaviour shown in the Klaviyo timing screenshot.
      //
      // The original triggerAfterScroll(offset, trigger) is kept below as a
      // no-op alias so any external code that calls it by name doesn't break.
      //
      triggerAfterScrollPercent(percent, trigger) {
        var opened = false;

        function onScroll() {
          if (opened) return;

          var scrollTop = window.pageYOffset ||
            (document.documentElement || document.body.parentNode || document.body).scrollTop;

          var scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;

          // Guard against pages that aren't tall enough to scroll (scrollableHeight = 0).
          if (scrollableHeight <= 0) return;

          var scrolled = (scrollTop / scrollableHeight) * 100;

          if (scrolled >= percent) {
            opened = true;
            window.removeEventListener('scroll', onScroll);
            trigger();
          }
        }

        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // Kept for backwards compatibility — any external calls to this method
      // by name continue to work. Internally we now use triggerAfterScrollPercent.
      triggerAfterScroll(offset, trigger) {
        var opened = false;
        window.addEventListener('scroll', function () {
          var scrollTop = window.pageYOffset ||
            (document.documentElement || document.body.parentNode || document.body).scrollTop;
          if (scrollTop > offset && !opened) {
            trigger();
            opened = true;
          }
        });
      }

      setRepeatOpen() {
        if (Shopify.designMode) return;
        if (this.repeatOpen === 'always_repeat') return;

        var expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_3_mins':
            expires = 3 / 60 / 24;
            break;
          case 'every_5_mins':
            expires = 5 / 60 / 24;
            break;
          case 'every_10_mins':
            expires = 1 / 6 / 24;
            break;
          case 'every_15_mins':
            expires = 15 / 60 / 24;
            break;
          case 'every_30_mins':
            expires = 1 / 2 / 24;
            break;
          case 'every_1_hr':
            expires = 1 / 24;
            break;
          case 'every_6_hrs':
            expires = 6 / 24;
            break;
          case 'every_12_hrs':
            expires = 1 / 2;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          case 'every_2_weeks':
            expires = 14;
            break;
          case 'every_month':
            expires = 30;
            break;
          default:
            expires = 7;
            break;
        }

        window.FoxTheme.utils.setStorage(this.cookieName, { opened: true }, expires);
      }
    }
  );
}

// The active Klaviyo form ID is resolved per-page in Liquid and exposed on the
// <subscription-popup> element as data-klaviyo-form-id. We only react to events
// for that form, so per-page custom forms hide-on-submit and close correctly.
// (Previously this was hardcoded to the single global form 'XmSNht'.)
window.addEventListener('klaviyoForms', function (e) {
  var popup = document.querySelector('subscription-popup');
  var activeFormId = popup && popup.dataset.klaviyoFormId;

  if (!activeFormId || e.detail.formId !== activeFormId) return;

  if (e.detail.type === 'submit') {
    document.querySelectorAll('.kl-hide-on-submit').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  if (e.detail.type === 'close') {
    if (popup) popup.hide();
  }
});